import './lib/zod-jitless.js'
import './app.css'
import { MutationCache, QueryCache, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { UpdateNotice } from './components/UpdateNotice.js'
import { ToastProvider } from './components/ui.js'
import { MOCK } from './env.js'
import { ApiError } from './lib/api.js'
import { BackendContext, createBackend } from './lib/backend.js'
import { isContentLockError } from './lib/content-lock.js'
import { installVisualViewport, requestPersistentStorage, updateOnResume } from './lib/ios-pwa.js'
import { captureLink, readPending } from './lib/links.js'
import { ONBOARDING_SHELL_PROBE } from './lib/onboarding-shell.js'
import { appQueryClient } from './lib/query-client.js'
import { endStaleSessionOnly } from './lib/session-only.js'
import { registerServiceWorker } from './lib/sw-register.js'
import { applyUpdatesInBrowser } from './lib/sw-update.js'
import { applyTheme, storedTheme } from './lib/theme.js'
import { trustedPolicy } from './lib/trusted-types.js'
import { makeRouter, onUnauthorized } from './router.js'

// A new worker can distinguish this invite-aware shell from a cached legacy bootstrap form.
navigator.serviceWorker?.addEventListener('message', (event) => {
  if (event.data?.type === ONBOARDING_SHELL_PROBE)
    event.ports[0]?.postMessage(ONBOARDING_SHELL_PROBE)
})

trustedPolicy()
applyTheme(storedTheme())
// Move `viko setup` / invite link secrets out of the URL before anything else sees it.
captureLink()
// A new link opened in this tab while the app runs (same-document fragment change): capture it
// and restart so the flow starts from the new link.
window.addEventListener('hashchange', () => {
  const before = readPending()?.at
  captureLink()
  if (readPending()?.at !== before) location.reload()
})

let onAuthLost: (() => void) | undefined
/** A list failed because this browser's content is locked: re-check the app-wide lock. */
let onLocked: (() => void) | undefined
const isUnauthorized = (e: unknown) => e instanceof ApiError && e.status === 401

const queryClient = appQueryClient({
  queryCache: new QueryCache({
    onError: (e, q) => {
      if (isUnauthorized(e) && q.queryKey[0] !== 'session') onAuthLost?.()
      else if (isContentLockError(e)) onLocked?.()
    },
  }),
  mutationCache: new MutationCache({
    onError: (e) => {
      if (isUnauthorized(e)) onAuthLost?.()
    },
  }),
})

async function boot() {
  const backend = await createBackend()
  // "Just this session" ended with the previous page (its keys lived in memory): sign out.
  await endStaleSessionOnly(backend.api).catch(() => false)
  const router = makeRouter({ queryClient, backend })
  onLocked = () => void backend.lock.check()
  onAuthLost = () => {
    onUnauthorized(queryClient, backend)
    void router.invalidate()
  }
  const root = document.getElementById('root')
  if (!root) throw new Error('#root missing')
  if (MOCK) document.documentElement.dataset.mock = '1'
  createRoot(root).render(
    <StrictMode>
      <BackendContext.Provider value={backend}>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <RouterProvider router={router} />
            <UpdateNotice />
          </ToastProvider>
        </QueryClientProvider>
      </BackendContext.Provider>
    </StrictMode>,
  )
  void registerServiceWorker().then((reg) => {
    if (!reg) return
    updateOnResume(reg)
    applyUpdatesInBrowser(reg)
  })
  // ---- ios-pwa: keyboard-aware viewport + storage eviction protection ----
  installVisualViewport()
  void requestPersistentStorage()
  // ---- end ios-pwa ----
}

void boot()
